import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { Card } from '@mtg-deck-tool/shared';
import { SwipeActionBar } from '../collection/SwipeActionBar.js';

import cardtraderSets from '../../data/cardtrader-sets.json';

const ctSets = cardtraderSets as Record<string, string>;

/** Build a CardTrader direct product URL: /en/cards/{card-slug}-{set-slug} */
function cardTraderUrl(card: Card): string {
  const searchFallback = `https://www.cardtrader.com/en/manasearch_results?q=${encodeURIComponent(card.name)}`;
  if (!card.set) return searchFallback;
  const slugify = (s: string) =>
    s.toLowerCase().replace(/'/g, '').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');
  const setSlug = ctSets[card.set.toLowerCase()];
  if (!setSlug) return searchFallback;
  return `https://www.cardtrader.com/en/cards/${slugify(card.name)}-${setSlug}`;
}
import { getCardImageUri, getCardManaCost, getCardTypeLine, isDoubleFaced, isLegalCommander } from '@mtg-deck-tool/shared';
import { useDeckStore } from '../../stores/deck-store.js';
import { useSearchStore } from '../../stores/search-store.js';
import { ManaCostIcons } from '../shared/ManaCostIcons.js';
import { useSymbolMap } from '../../hooks/useSymbolMap.js';
import { useCardTags } from '../../hooks/useCardTags.js';
import { useCardCombos } from '../../hooks/useCardCombos.js';
import { useEdhrecCards } from '../../hooks/useEdhrecCards.js';
import { useCommanderThemes } from '../../hooks/useCommanderThemes.js';
import { api } from '../../lib/api-client.js';
import { useCardPreview } from '../../contexts/CardPreviewContext.js';

// ─── EdhrecCardRow ─────────────────────────────────────────────────────────────

interface EdhrecCardRowProps {
  name: string;
  numDecks: number;
  synergy: number;
  lift?: number;
  onHoverStart: (name: string) => void;
  onHoverEnd: () => void;
}

function EdhrecCardRow({ name, numDecks, synergy, lift, onHoverStart, onHoverEnd }: EdhrecCardRowProps) {
  const openCardDetail = useDeckStore((s) => s.openCardDetail);
  const addCard = useDeckStore((s) => s.addCard);
  const addToSideboard = useDeckStore((s) => s.addToSideboard);

  const [addingToDeck, setAddingToDeck] = useState(false);
  const [addingToSideboard, setAddingToSideboard] = useState(false);
  const [resolveError, setResolveError] = useState(false);

  async function resolveCard(): Promise<Card | null> {
    try {
      const result = await api.resolveCards([name]);
      return result.found[0] ?? null;
    } catch {
      return null;
    }
  }

  async function handleOpenDetail() {
    const card = await resolveCard();
    if (card) {
      openCardDetail(card, false);
    }
  }

  async function handleAddToDeck() {
    setAddingToDeck(true);
    setResolveError(false);
    const card = await resolveCard();
    if (card) {
      addCard(card);
    } else {
      setResolveError(true);
    }
    setAddingToDeck(false);
  }

  async function handleAddToSideboard() {
    setAddingToSideboard(true);
    setResolveError(false);
    const card = await resolveCard();
    if (card) {
      addToSideboard(card);
    } else {
      setResolveError(true);
    }
    setAddingToSideboard(false);
  }

  return (
    <div className="flex items-center gap-2 py-1">
      <button
        onClick={handleOpenDetail}
        onMouseEnter={() => onHoverStart(name)}
        onMouseLeave={onHoverEnd}
        className="flex-1 text-sm text-gray-200 truncate hover:text-orange-300 hover:underline cursor-pointer bg-transparent border-none p-0 text-left"
      >
        {name}
      </button>
      <span className="text-[10px] text-blue-400/70 shrink-0 tabular-nums">
        {lift !== undefined
          ? `${lift >= 0 ? '+' : ''}${lift.toFixed(1)}x`
          : `${synergy >= 0 ? '+' : ''}${Math.round(synergy * 100)}%`}
      </span>
      {resolveError && (
        <span className="text-[10px] text-red-400 shrink-0">Not found</span>
      )}
      <button
        onClick={handleAddToDeck}
        disabled={addingToDeck}
        className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white rounded transition-colors border border-orange-700/40 hover:border-orange-600 shrink-0 disabled:opacity-50"
        title="Add to Deck"
      >
        {addingToDeck ? '...' : '+'}
      </button>
      <button
        onClick={handleAddToSideboard}
        disabled={addingToSideboard}
        className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white rounded transition-colors border border-blue-700/40 hover:border-blue-600 shrink-0 disabled:opacity-50"
        title="Add to Sideboard"
      >
        {addingToSideboard ? '...' : '?'}
      </button>
    </div>
  );
}

// ─── ComboPartnerCard ─────────────────────────────────────────────────────────

interface ComboPartnerCardProps {
  name: string;
  onHoverStart: (name: string) => void;
  onHoverEnd: () => void;
}

function ComboPartnerCard({ name, onHoverStart, onHoverEnd }: ComboPartnerCardProps) {
  const openCardDetail = useDeckStore((s) => s.openCardDetail);
  const addCard = useDeckStore((s) => s.addCard);
  const addToSideboard = useDeckStore((s) => s.addToSideboard);

  const [addingToDeck, setAddingToDeck] = useState(false);
  const [addingToSideboard, setAddingToSideboard] = useState(false);
  const [resolveError, setResolveError] = useState(false);

  async function resolveCard() {
    try {
      const result = await api.resolveCards([name]);
      return result.found[0] ?? null;
    } catch {
      return null;
    }
  }

  async function handleOpenDetail() {
    const card = await resolveCard();
    if (card) {
      openCardDetail(card, false);
    }
  }

  async function handleAddToDeck() {
    setAddingToDeck(true);
    setResolveError(false);
    const card = await resolveCard();
    if (card) {
      addCard(card);
    } else {
      setResolveError(true);
    }
    setAddingToDeck(false);
  }

  async function handleAddToSideboard() {
    setAddingToSideboard(true);
    setResolveError(false);
    const card = await resolveCard();
    if (card) {
      addToSideboard(card);
    } else {
      setResolveError(true);
    }
    setAddingToSideboard(false);
  }

  return (
    <div className="flex items-center gap-2 py-0.5">
      <button
        onClick={handleOpenDetail}
        onMouseEnter={() => onHoverStart(name)}
        onMouseLeave={onHoverEnd}
        className="flex-1 text-sm text-orange-300 hover:text-orange-100 hover:underline cursor-pointer bg-transparent border-none p-0 text-left"
      >
        {name}
      </button>
      {resolveError && (
        <span className="text-[10px] text-red-400 shrink-0">Not found</span>
      )}
      <button
        onClick={handleAddToDeck}
        disabled={addingToDeck}
        className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white rounded transition-colors border border-orange-700/40 hover:border-orange-600 shrink-0 disabled:opacity-50"
        title="Add to Deck"
      >
        {addingToDeck ? '...' : '+'}
      </button>
      <button
        onClick={handleAddToSideboard}
        disabled={addingToSideboard}
        className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white rounded transition-colors border border-blue-700/40 hover:border-blue-600 shrink-0 disabled:opacity-50"
        title="Add to Sideboard"
      >
        {addingToSideboard ? '...' : '?'}
      </button>
    </div>
  );
}

// ─── ComboRow ─────────────────────────────────────────────────────────────────

interface ComboRowProps {
  cards: string[];
  result: string;
  queriedCardName: string;
  onHoverStart: (name: string) => void;
  onHoverEnd: () => void;
}

function ComboRow({ cards, result, queriedCardName, onHoverStart, onHoverEnd }: ComboRowProps) {
  const otherCards = cards.filter(
    (c) => c.toLowerCase() !== queriedCardName.toLowerCase(),
  );

  return (
    <div className="py-1.5 border-b border-gray-800/50 last:border-0">
      <div className="flex flex-col gap-0.5 mb-0.5">
        {otherCards.map((name) => (
          <ComboPartnerCard key={name} name={name} onHoverStart={onHoverStart} onHoverEnd={onHoverEnd} />
        ))}
        {otherCards.length === 0 && (
          <span className="text-sm text-gray-500 italic">No other cards</span>
        )}
      </div>
      <div className="text-xs text-gray-500 italic">→ {result}</div>
    </div>
  );
}

// ─── CardDetailModal ──────────────────────────────────────────────────────────

interface CardDetailModalProps {
  onOpenEditor?: () => void;
}

export function CardDetailModal({ onOpenEditor }: CardDetailModalProps) {
  const cardDetailTarget = useDeckStore((s) => s.cardDetailTarget);
  const closeCardDetail = useDeckStore((s) => s.closeCardDetail);
  const setUserInput = useSearchStore((s) => s.setUserInput);
  const triggerSearch = useSearchStore((s) => s.triggerSearch);
  const flippedCards = useDeckStore((s) => s.flippedCards);
  const toggleFlip = useDeckStore((s) => s.toggleFlip);
  const entries = useDeckStore((s) => s.entries);
  const sideboard = useDeckStore((s) => s.sideboard);
  const addCard = useDeckStore((s) => s.addCard);
  const removeCard = useDeckStore((s) => s.removeCard);
  const addToSideboard = useDeckStore((s) => s.addToSideboard);
  const removeFromSideboard = useDeckStore((s) => s.removeFromSideboard);
  const moveToDeck = useDeckStore((s) => s.moveToDeck);
  const symbolMap = useSymbolMap();
  const { setHoveredCard } = useCardPreview();

  const card = cardDetailTarget?.card ?? null;
  const isCommander = cardDetailTarget?.isCommander ?? false;
  const discoveryMode = cardDetailTarget?.discoveryMode ?? false;
  const reasons = cardDetailTarget?.reasons;
  const swipeMode = cardDetailTarget?.swipeMode ?? null;

  const fallbackThemes = reasons
    ? reasons
        .map((r) => {
          if (r.startsWith('Matches strategy gap: ')) {
            return r.replace('Matches strategy gap: ', '');
          }
          if (r.startsWith("Shares '") && r.endsWith("' mechanics")) {
            return r.slice(8, -11);
          }
          return null;
        })
        .filter((theme): theme is string => theme !== null)
    : [];

  const [buildingDeck, setBuildingDeck] = useState(false);

  // In swipe mode: skip Tagger (tags) and Spellbook (combos) fetches entirely —
  // these APIs are not needed in swipe mode and Tagger was 429-spamming.
  const { tags, isLoading: tagsLoading, isError: tagsError } = useCardTags(swipeMode ? null : card);
  const { combos, isLoading: combosLoading } = useCardCombos(swipeMode ? '' : (card?.name ?? ''));
  // Use commander endpoint (highsynergycards) for any card that can legally be
  // a commander — legendary creatures, and cards with "can be your commander".
  const useCommanderEndpoint = isCommander || (card !== null && isLegalCommander(card));

  // Save requests by only fetching EDHREC synergy cards when NOT in discovery mode.
  // In swipe mode we DO fetch synergy cards (the section is kept).
  const { cards: edhrecCards, isLoading: edhrecLoading } = useEdhrecCards(
    discoveryMode ? '' : (card?.name ?? ''),
    useCommanderEndpoint,
  );

  const { themes: commanderThemes, isLoading: themesLoading, isError: themesError } = useCommanderThemes(
    discoveryMode && card ? card.name : ''
  );

  const displayThemes = Array.from(
    new Set([...fallbackThemes, ...(commanderThemes || [])])
  );

  async function handleBuildCommanderDeck() {
    if (!card) return;
    setBuildingDeck(true);
    try {
      const newId = `deck-${Date.now()}`;
      const deckName = `${card.name} Deck`;
      const colorIdentity = card.colorIdentity || [];
      const commanderNames = [card.name];
      
      // Save deck to collection database
      await api.saveCollectionDeck({
        id: newId,
        name: deckName,
        colorIdentity,
        commanderNames,
        cardCount: 1,
        entries: [
          {
            cardName: card.name,
            oracleId: card.oracleId,
            setCode: card.set,
            collectorNumber: card.collectorNumber,
            quantity: 1,
            isCommander: true,
            isSideboard: false,
          }
        ],
      });
      
      // Hydrate local deck store state
      useDeckStore.setState({
        deckName,
        entries: [
          {
            card,
            quantity: 1,
            isCommander: true,
          }
        ],
        sideboard: [],
        moxfieldDeckId: null,
        colorIdentity,
        tagProfile: null,
        tagHierarchy: null,
        isProfileStale: true,
        importGeneration: (useDeckStore.getState().importGeneration ?? 0) + 1,
      });

      // Clear card preview hover state
      setHoveredCard(null);
      
      // Close modal
      closeCardDetail();
      
      // Trigger navigation
      if (onOpenEditor) {
        onOpenEditor();
      }
    } catch (err) {
      console.error('[build-commander-deck-error]', err);
    } finally {
      setBuildingDeck(false);
    }
  }

  // ── Hover preview ──────────────────────────────────────────────────────────
  const [hoveredCardName, setHoveredCardName] = useState<string | null>(null);
  const [hoveredImageUri, setHoveredImageUri] = useState<string | null>(null);

  // Reset hover state when a different card is opened
  useEffect(() => {
    setHoveredCardName(null);
    setHoveredImageUri(null);
  }, [cardDetailTarget]);
  // Cache resolved image URIs so repeated hovers don't re-fetch
  const imageCache = useRef<Map<string, string | null>>(new Map());

  useEffect(() => {
    if (!hoveredCardName) {
      setHoveredImageUri(null);
      return;
    }
    // Return from cache immediately
    if (imageCache.current.has(hoveredCardName)) {
      setHoveredImageUri(imageCache.current.get(hoveredCardName) ?? null);
      return;
    }
    let cancelled = false;
    // hoveredCardName is non-null here — the early return above guards it
    const nameAtStart: string = hoveredCardName;
    api.resolveCards([nameAtStart]).then((result) => {
      if (cancelled) return;
      const resolved = result.found[0] ?? null;
      const uri: string | null = resolved ? (getCardImageUri(resolved, 'large') ?? null) : null;
      imageCache.current.set(nameAtStart, uri);
      setHoveredImageUri(uri);
    }).catch(() => {
      if (!cancelled) imageCache.current.set(nameAtStart, null);
    });
    return () => { cancelled = true; };
  }, [hoveredCardName]);

  function handleHoverStart(name: string) { setHoveredCardName(name); }
  function handleHoverEnd() { setHoveredCardName(null); }

  // Escape key handler
  useEffect(() => {
    if (!cardDetailTarget) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setHoveredCard(null);
        if (swipeMode) {
          swipeMode.onPause();
        } else {
          closeCardDetail();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [cardDetailTarget, closeCardDetail, swipeMode]);

  if (!cardDetailTarget || !card) return null;

  const isFlipped = flippedCards.has(card.oracleId);
  const faceIndex = isFlipped ? 1 : 0;
  const ownImageUri = getCardImageUri(card, 'large', faceIndex);
  // While hovering a related card, show its image instead
  const imageUri = hoveredImageUri ?? ownImageUri;
  const manaCost = getCardManaCost(card);
  const typeLine = getCardTypeLine(card);
  const price = card.prices.eur;
  const isDfc = isDoubleFaced(card);

  function handleClose() {
    setHoveredCard(null);
    if (swipeMode) {
      swipeMode.onPause();
    } else {
      closeCardDetail();
    }
  }

  const modal = (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center px-4 py-8"
      onClick={handleClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="card-detail-name"
        data-testid="card-detail-modal"
        data-swipe-mode={swipeMode ? 'true' : undefined}
        className={`bg-gray-900 rounded-xl border border-gray-700 w-full max-w-4xl mx-auto relative flex flex-col ${swipeMode ? 'max-h-[85vh]' : 'max-h-[90vh]'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Swipe mode: progress indicator */}
        {swipeMode && (
          <div className="shrink-0 flex items-center justify-between px-4 pt-2 pb-0">
            <span className="text-xs text-gray-500 tabular-nums">{swipeMode.progressText}</span>
            {swipeMode.isNew && (
              <span
                className="text-xs font-semibold bg-emerald-600/90 text-white px-2 py-0.5 rounded-full"
                data-testid="swipe-new-commander-badge"
              >
                NEW
              </span>
            )}
          </div>
        )}

        {/* Close button */}
        <button
          onClick={handleClose}
          aria-label="Close card detail"
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-full transition-colors z-10 text-sm"
        >
          ✕
        </button>

        {/* ── Fixed top: card image + name/type/price only (never scrolls) ── */}
        <div className={`shrink-0 ${swipeMode ? 'p-3 pb-2' : 'p-6 pb-4'}`}>
          <div className={`flex ${swipeMode ? 'gap-4' : 'gap-6'}`}>
            {/* Left: card image */}
            <div className="shrink-0">
              {imageUri ? (
                <div className="relative">
                  <img
                    src={imageUri}
                    alt={card.name}
                    className={`rounded-xl shadow-lg border border-gray-700/50 ${swipeMode ? 'w-[200px]' : 'w-[300px]'}`}
                  />
                  {isDfc && (
                    <button
                      onClick={() => toggleFlip(card.oracleId)}
                      className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-gray-900/80 border border-gray-600 text-gray-300 hover:text-white hover:bg-gray-700 flex items-center justify-center text-sm transition-colors"
                      title="Flip card"
                    >
                      &#x21BB;
                    </button>
                  )}
                </div>
              ) : (
                <div className={`${swipeMode ? 'w-[200px] h-[279px]' : 'w-[300px] h-[418px]'} bg-gray-800 rounded-xl border border-gray-700/50 flex items-center justify-center text-gray-600 text-xs`}>
                  {card.name}
                </div>
              )}
            </div>

            {/* Right: name + mana cost + type + oracle text + price
                Scrolls independently so long oracle text never pushes the image out of view */}
            <div className={`flex-1 min-w-0 pt-1 flex flex-col overflow-y-auto ${swipeMode ? 'max-h-[279px]' : 'max-h-[418px]'} pr-1`}>
              <div className="flex items-start justify-between gap-2 mb-2 pr-8">
                <h2 id="card-detail-name" className="text-2xl font-bold text-gray-100 leading-tight">
                  {card.name}
                </h2>
                {manaCost && (
                  <div className="flex items-center shrink-0 mt-1">
                    <ManaCostIcons manaCost={manaCost} symbolMap={symbolMap} size={20} />
                  </div>
                )}
              </div>
              {(() => {
                const faceTypeLine = card.cardFaces?.[faceIndex]?.typeLine ?? typeLine;
                return faceTypeLine ? (
                  <p className="text-sm text-gray-400 pb-3 mb-3 border-b border-gray-700">{faceTypeLine}</p>
                ) : null;
              })()}
              {(() => {
                const oracleText = card.cardFaces?.[faceIndex]?.oracleText ?? card.oracleText;
                return oracleText ? (
                  <p className="text-base text-gray-300 whitespace-pre-line leading-relaxed mb-4 flex-1">
                    {oracleText}
                  </p>
                ) : null;
              })()}
              {price && (
                <div className="flex items-center gap-2">
                  <p className="text-sm text-green-400 font-medium">€{price}</p>
                  <a
                    href={cardTraderUrl(card)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2 py-0.5 text-[10px] bg-purple-900/40 hover:bg-purple-800/60 text-purple-400 hover:text-purple-300 border border-purple-800/50 rounded transition-colors"
                    title="Buy on CardTrader"
                  >
                    CardTrader
                  </a>
                </div>
              )}

              {/* Action buttons — hidden in swipe mode */}
              {card && !swipeMode && (() => {
                if (discoveryMode) {
                  return (
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={handleBuildCommanderDeck}
                        disabled={buildingDeck}
                        className="px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white font-bold text-xs rounded-xl shadow-lg shadow-orange-950/40 hover:scale-[1.02] hover:shadow-orange-500/10 active:scale-95 transition-all duration-200 shrink-0 disabled:opacity-50"
                      >
                        {buildingDeck ? 'Building...' : 'Build Commander Deck'}
                      </button>
                    </div>
                  );
                }

                const inDeck = entries.some((e) => e.card.oracleId === card.oracleId);
                const inSideboard = sideboard.some((c) => c.card.oracleId === card.oracleId);
                const deckEntry = entries.find((e) => e.card.oracleId === card.oracleId);
                const isCmd = deckEntry?.isCommander ?? false;

                return (
                  <div className="flex items-center gap-2 mt-3">
                    {inDeck && !isCmd && (
                      <>
                        <button
                          onClick={() => { if (deckEntry) removeCard(deckEntry.card.id); }}
                          className="px-3 py-1 text-xs bg-red-900/40 hover:bg-red-800/60 text-red-400 hover:text-red-300 border border-red-800/50 rounded transition-colors"
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => { if (deckEntry) { removeCard(deckEntry.card.id); addToSideboard(card); } }}
                          className="px-3 py-1 text-xs bg-blue-900/40 hover:bg-blue-800/60 text-blue-400 hover:text-blue-300 border border-blue-800/50 rounded transition-colors"
                        >
                          To Sideboard
                        </button>
                      </>
                    )}
                    {inSideboard && (
                      <>
                        <button
                          onClick={() => moveToDeck(card.oracleId)}
                          className="px-3 py-1 text-xs bg-green-900/40 hover:bg-green-800/60 text-green-400 hover:text-green-300 border border-green-800/50 rounded transition-colors"
                        >
                          Add to Deck
                        </button>
                        <button
                          onClick={() => removeFromSideboard(card.oracleId)}
                          className="px-3 py-1 text-xs bg-red-900/40 hover:bg-red-800/60 text-red-400 hover:text-red-300 border border-red-800/50 rounded transition-colors"
                        >
                          Remove
                        </button>
                      </>
                    )}
                    {!inDeck && !inSideboard && (
                      <>
                        <button
                          onClick={() => addCard(card)}
                          className="px-3 py-1 text-xs bg-green-900/40 hover:bg-green-800/60 text-green-400 hover:text-green-300 border border-green-800/50 rounded transition-colors"
                        >
                          Add to Deck
                        </button>
                        <button
                          onClick={() => addToSideboard(card)}
                          className="px-3 py-1 text-xs bg-blue-900/40 hover:bg-blue-800/60 text-blue-400 hover:text-blue-300 border border-blue-800/50 rounded transition-colors"
                        >
                          To Sideboard
                        </button>
                      </>
                    )}
                    {inDeck && isCmd && (
                      <span className="text-xs text-gray-500 italic">Commander</span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ── Scrollable: tags/combos (normal) OR strategic themes (swipe) + synergy cards ── */}
        {/* In swipe mode: pb-24 ensures content scrolls clear of the pinned SwipeActionBar (~88px) */}
        <div
          data-testid="card-detail-scroll-area"
          className={`overflow-y-auto px-6 pt-4 border-t border-gray-800 ${swipeMode ? 'pb-24' : 'pb-6'}`}
        >

          {swipeMode ? (
            /* ── Swipe mode: Strategic Themes + High Synergy Cards only ─────── */
            <>
              {/* Strategic Themes — from deal payload; no network fetch */}
              <div className="mb-4" data-testid="swipe-strategic-themes">
                <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                  Strategic Themes
                </h3>
                {swipeMode.hasEdhrecData && swipeMode.themes.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {swipeMode.themes.map((theme) => (
                      <span
                        key={theme.slug}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-orange-950/30 border border-orange-500/20 text-orange-400 font-bold rounded-xl shadow-md"
                      >
                        {theme.displayName}
                        {theme.deckCount !== undefined && (
                          <span className="text-orange-500/60 font-normal tabular-nums">
                            {theme.deckCount.toLocaleString()}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No theme data yet — sync pending
                  </p>
                )}
              </div>

              {/* High Synergy Cards — EDHREC fetch, hover previews intact */}
              {(edhrecLoading || edhrecCards.length > 0) && (
                <div className="mb-4">
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    High Synergy Cards
                  </h3>
                  {edhrecLoading && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-3 bg-gray-800 rounded animate-pulse" />
                      ))}
                    </div>
                  )}
                  {!edhrecLoading && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0">
                      {edhrecCards.map((edhrecCard) => (
                        <EdhrecCardRow
                          key={edhrecCard.name}
                          name={edhrecCard.name}
                          numDecks={edhrecCard.numDecks}
                          synergy={edhrecCard.synergy}
                          {...(edhrecCard.lift !== undefined ? { lift: edhrecCard.lift } : {})}
                          onHoverStart={handleHoverStart}
                          onHoverEnd={handleHoverEnd}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* ── Normal / discovery mode: Tags + EDHREC section + Combos ───── */
            <>
              {/* Tags section */}
              <div className="mb-4">
                <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                  Oracle Tags
                </h3>
                {tagsLoading && (
                  <div className="space-y-1.5">
                    <div className="h-3 bg-gray-800 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-gray-800 rounded animate-pulse w-1/2" />
                    <div className="h-3 bg-gray-800 rounded animate-pulse w-2/3" />
                  </div>
                )}
                {!tagsLoading && tagsError && (
                  <p className="text-sm text-gray-500 italic">Tags unavailable</p>
                )}
                {!tagsLoading && !tagsError && tags.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No tags</p>
                )}
                {!tagsLoading && !tagsError && tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => {
                          const slug = tag.toLowerCase().replace(/\s+/g, '-');
                          setUserInput(`otag:${slug}`);
                          triggerSearch();
                          setHoveredCard(null);
                          closeCardDetail();
                        }}
                        className="px-2 py-0.5 text-xs bg-gray-800 hover:bg-orange-600/20 text-gray-300 hover:text-orange-300 border border-gray-700 hover:border-orange-700/50 rounded transition-colors cursor-pointer"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* EDHREC section: Popular Archetypes (Discovery) or High Synergy/Lift (Normal) */}
              {discoveryMode ? (
                <div className="mb-4">
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    Popular Archetypes
                  </h3>
                  {themesLoading && displayThemes.length === 0 && (
                    <div className="space-y-1.5">
                      <div className="h-3 bg-gray-800 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-gray-800 rounded animate-pulse w-1/2" />
                    </div>
                  )}
                  {!themesLoading && themesError && displayThemes.length === 0 && (
                    <p className="text-sm text-gray-500 italic">Archetypes unavailable</p>
                  )}
                  {displayThemes.length === 0 && !themesLoading && !themesError && (
                    <p className="text-sm text-gray-500 italic">No popular archetypes listed</p>
                  )}
                  {displayThemes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {displayThemes.map((theme) => (
                        <span
                          key={theme}
                          className="px-3 py-1 text-xs bg-orange-950/30 border border-orange-500/20 text-orange-400 font-bold rounded-xl shadow-md"
                        >
                          {theme}
                        </span>
                      ))}
                      {themesLoading && (
                        <span className="w-3.5 h-3.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin ml-2 shrink-0" title="Loading more archetypes..." />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                (edhrecLoading || edhrecCards.length > 0) && (
                  <div className="mb-4">
                    <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                      {useCommanderEndpoint ? 'High Synergy Cards' : 'High Lift Cards'}
                    </h3>
                    {edhrecLoading && (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="h-3 bg-gray-800 rounded animate-pulse" />
                        ))}
                      </div>
                    )}
                    {!edhrecLoading && (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0">
                        {edhrecCards.map((edhrecCard) => (
                          <EdhrecCardRow
                            key={edhrecCard.name}
                            name={edhrecCard.name}
                            numDecks={edhrecCard.numDecks}
                            synergy={edhrecCard.synergy}
                            {...(edhrecCard.lift !== undefined ? { lift: edhrecCard.lift } : {})}
                            onHoverStart={handleHoverStart}
                            onHoverEnd={handleHoverEnd}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )
              )}

              {/* Combos section */}
              {(combosLoading || combos.length > 0) && (
                <div className="mb-2">
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    Combos
                  </h3>
                  {combosLoading && (
                    <div className="space-y-1.5">
                      <div className="h-3 bg-gray-800 rounded animate-pulse w-full" />
                      <div className="h-3 bg-gray-800 rounded animate-pulse w-4/5" />
                    </div>
                  )}
                  {!combosLoading && (
                    <div>
                      {combos.map((combo, i) => (
                        <ComboRow
                          key={i}
                          cards={combo.cards}
                          result={combo.result}
                          queriedCardName={card.name}
                          onHoverStart={handleHoverStart}
                          onHoverEnd={handleHoverEnd}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Swipe mode: action bar pinned to the bottom of the modal */}
        {swipeMode && (
          <div className="shrink-0 border-t border-gray-800 px-6 pb-2">
            <SwipeActionBar
              onLike={swipeMode.onLike}
              onDislike={swipeMode.onDislike}
              onSkip={swipeMode.onSkip}
            />
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
